import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { clamp } from "@perceptron/utils/math";
import Konva from "konva";
import { Group, Layer, Rect } from "react-konva";

import {
  CORNER_RADIUS,
  PLAYHEAD_GLOW_COLORS,
  PLAYHEAD_GLOW_HEIGHT,
  PLAYHEAD_GLOW_WIDTH,
  VIDEO_TRACK_UNPLAYED_COLOR,
} from "../../constants";

interface VideoTrackLayerProps {
  durationSeconds: number;
  pixelsPerSecond: number;
  scrollLeft: number;
}

export interface VideoTrackLayerHandle {
  updateProgress: (time: number) => void;
}

const VIDEO_TRACK_HEIGHT = 16;
const BORDER_WIDTH = 1;

const BORDER_STROKE_GRADIENT = [
  0,
  "hsla(0, 0%, 100%, 0.05)",
  0.12,
  "hsla(0, 0%, 12%, 0.02)",
  0.82,
  "hsla(0, 0%, 40%, 0.08)",
  1,
  "hsla(0, 0%, 100%, 0.2)",
];

export const VideoTrackLayer = forwardRef<VideoTrackLayerHandle, VideoTrackLayerProps>(function VideoTrackLayer(
  { durationSeconds, pixelsPerSecond, scrollLeft },
  ref,
) {
  const playedRectRef = useRef<Konva.Rect>(null);
  const unplayedRectRef = useRef<Konva.Rect>(null);
  const glowRectRef = useRef<Konva.Rect>(null);
  const layerRef = useRef<Konva.Layer>(null);

  const currentTimeRef = useRef(0);

  const totalWidth = useMemo(() => durationSeconds * pixelsPerSecond, [durationSeconds, pixelsPerSecond]);

  const halfBorder = BORDER_WIDTH / 2;
  const borderRectWidth = clamp(totalWidth - BORDER_WIDTH, 0, totalWidth);
  const borderRectHeight = clamp(VIDEO_TRACK_HEIGHT - BORDER_WIDTH, 0, VIDEO_TRACK_HEIGHT);

  const updateProgress = useCallback(
    (time: number) => {
      const played = playedRectRef.current;
      const unplayed = unplayedRectRef.current;
      const layer = layerRef.current;
      if (!played || !unplayed || !layer) return;

      const clampedTime = clamp(time, 0, durationSeconds);
      currentTimeRef.current = clampedTime;

      const playedWidth = clamp(clampedTime * pixelsPerSecond, 0, totalWidth);
      const unplayedWidth = clamp(totalWidth - playedWidth, 0, totalWidth);

      // Played rect — corner rounding now comes from the parent Group's clipFunc,
      // so we no longer need to rewrite cornerRadius based on whether played has
      // reached the right edge.
      played.width(playedWidth);
      played.visible(playedWidth > 0);
      if (playedWidth > 0) {
        played.fillLinearGradientEndPoint({
          x: playedWidth,
          y: 0,
        });
      }

      // Unplayed rect
      unplayed.x(playedWidth);
      unplayed.width(unplayedWidth);
      unplayed.visible(unplayedWidth > 0);

      // Comet-trail glow — right edge sits at the playhead, gradient fades back into the track.
      const glow = glowRectRef.current;
      if (glow) {
        glow.x(playedWidth - PLAYHEAD_GLOW_WIDTH);
      }

      layer.batchDraw();
    },
    [durationSeconds, pixelsPerSecond, totalWidth],
  );

  // Trace the scrub bar's rounded-rect outline so the inner Group clips
  // played / unplayed / glow to that silhouette. The radius is clamped to half
  // the box dimensions so very narrow tracks don't cause invalid arc geometry.
  const trackClipFunc = useCallback(
    (ctx: Konva.Context) => {
      const r = Math.min(CORNER_RADIUS, totalWidth / 2, VIDEO_TRACK_HEIGHT / 2);
      const w = totalWidth;
      const h = VIDEO_TRACK_HEIGHT;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(w - r, 0);
      ctx.arcTo(w, 0, w, r, r);
      ctx.lineTo(w, h - r);
      ctx.arcTo(w, h, w - r, h, r);
      ctx.lineTo(r, h);
      ctx.arcTo(0, h, 0, h - r, r);
      ctx.lineTo(0, r);
      ctx.arcTo(0, 0, r, 0, r);
      ctx.closePath();
    },
    [totalWidth],
  );

  useImperativeHandle(
    ref,
    () => ({
      updateProgress,
    }),
    [updateProgress],
  );

  // Reapply the last imperative time when the layout or duration changes.
  useEffect(() => {
    updateProgress(currentTimeRef.current);
  }, [updateProgress]);

  return (
    <Layer ref={layerRef} x={-scrollLeft}>
      {/* Inner Group is clipped to the scrub bar's rounded-rect outline so the
          played fill, unplayed fill, and comet-trail glow share the same
          shape mask. Without this, the glow `Rect` (which has no own
          `cornerRadius`) paints square corners over the bar's rounded ends. */}
      <Group clipFunc={trackClipFunc} y={0}>
        {/* Played section */}
        <Rect
          cornerRadius={0}
          fill="white"
          height={VIDEO_TRACK_HEIGHT}
          listening={false}
          ref={playedRectRef}
          visible={false}
          width={0}
        />

        {/* Unplayed section — its own cornerRadius is no longer needed (the
            Group's clipFunc supplies the rounded silhouette). */}
        <Rect
          cornerRadius={0}
          fill={VIDEO_TRACK_UNPLAYED_COLOR}
          height={VIDEO_TRACK_HEIGHT}
          listening={false}
          ref={unplayedRectRef}
          visible={totalWidth > 0}
          width={totalWidth}
          x={0}
          y={0}
        />

        {/* Comet-trail glow — right edge sits at the playhead. Clipped by the
            Group above to the scrub bar's rounded corners. */}
        <Rect
          fillLinearGradientColorStops={[
            0,
            PLAYHEAD_GLOW_COLORS[0],
            0.5,
            PLAYHEAD_GLOW_COLORS[1],
            0.75,
            PLAYHEAD_GLOW_COLORS[2],
            1,
            PLAYHEAD_GLOW_COLORS[3],
          ]}
          fillLinearGradientEndPoint={{ x: PLAYHEAD_GLOW_WIDTH, y: 0 }}
          fillLinearGradientStartPoint={{ x: 0, y: 0 }}
          height={PLAYHEAD_GLOW_HEIGHT}
          listening={false}
          ref={glowRectRef}
          width={PLAYHEAD_GLOW_WIDTH}
          x={-PLAYHEAD_GLOW_WIDTH}
          y={0}
        />
      </Group>

      {/* Border — sibling of the clipped Group, so it paints over the rounded
          silhouette's edge instead of being clipped by it. */}
      <Rect
        cornerRadius={CORNER_RADIUS}
        fillEnabled={false}
        height={borderRectHeight}
        hitStrokeWidth={0}
        listening={false}
        strokeLinearGradientColorStops={BORDER_STROKE_GRADIENT}
        strokeLinearGradientEndPoint={{ x: 0, y: 0 }}
        strokeLinearGradientStartPoint={{ x: 0, y: borderRectHeight }}
        strokeWidth={BORDER_WIDTH}
        width={borderRectWidth}
        x={halfBorder}
        y={halfBorder}
      />
    </Layer>
  );
});

VideoTrackLayer.displayName = "VideoTrackLayer";
